
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

/* -------------------------------------------------
   Logo component
   ------------------------------------------------- */
function Logo({ isScrolled, size = 'normal' }: { isScrolled: boolean; size?: 'normal' | 'small' }) {
  const isSmall = size === 'small';
  
  return (
    <div className="flex items-center space-x-2">
      <div className={`relative flex items-center justify-center ${isSmall ? 'w-7 h-8' : 'w-10 h-12'}`}>
        {/* Magazine shape */}
        <div className="absolute inset-0 bg-white rounded-sm shadow-md overflow-hidden">
          {/* Gradient wave */}
          <svg viewBox="0 0 40 48" className="absolute inset-0 w-full h-full">
            <defs>
              <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6B4EFF" />
                <stop offset="50%" stopColor="#9B6DFF" />
                <stop offset="100%" stopColor="#FF4D7D" />
              </linearGradient>
            </defs>
            {/* Wave pattern */}
            <path
              d="M0 32 Q10 28, 20 32 T40 32 L40 48 L0 48 Z"
              fill="url(#logoGradient)"
            />
            <path
              d="M0 36 Q10 32, 20 36 T40 36 L40 48 L0 48 Z"
              fill="url(#logoGradient)"
              opacity="0.6"
            />
          </svg>
          {/* Magazine lines */}
          <div className={`absolute space-y-0.5 ${isSmall ? 'top-1 left-1 right-1' : 'top-2 left-2 right-2 space-y-1'}`}>
            <div className={`bg-gradient-to-r from-[#6B4EFF] to-[#FF4D7D] rounded-full w-4/5 ${isSmall ? 'h-0.5' : 'h-1'}`}></div>
            <div className={`bg-gray-300 rounded-full w-full ${isSmall ? 'h-px' : 'h-0.5'}`}></div>
            <div className={`bg-gray-300 rounded-full w-3/4 ${isSmall ? 'h-px' : 'h-0.5'}`}></div>
            <div className={`bg-gray-200 rounded-full w-5/6 ${isSmall ? 'h-px' : 'h-0.5'}`}></div>
          </div>
        </div>
        {/* Spine effect */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#6B4EFF] to-[#FF4D7D] rounded-l-sm"></div>
      </div>
      <span
        className={`font-semibold transition-colors ${
          isSmall ? 'text-sm text-gray-900' : 'text-lg'
        } ${!isSmall && (isScrolled ? 'text-gray-900' : 'text-white')}`}
      >
        Harmony Horizon
      </span>
    </div>
  );
}

/* -------------------------------------------------
   Navbar component
   ------------------------------------------------- */
export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const isAdvertisersPage = location.pathname === '/advertisers';

  /* ---------- Scroll listener ---------- */
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup on unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* ---------- Helper: smooth scroll ---------- */
  const scrollToSection = (id: string) => {
    try {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        console.warn(`Navbar: Element with id "${id}" not found.`);
      }
    } catch (err) {
      console.error('Navbar: scrollToSection failed', err);
    }
  };

  return (
    <>
      {/* ========== MOBILE FIXED TOP BAR ========== */}
      <nav className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-sm">
        <div className="flex items-center justify-between px-4 h-14">
          {/* Left: Logo + Name */}
          <Link to="/" className="flex items-center cursor-pointer">
            <Logo isScrolled={true} size="small" />
          </Link>

          {/* Right: For Advertisers link (always visible) */}
          {!isAdvertisersPage ? (
            <Link
              to="/advertisers"
              className="flex items-center justify-center h-11 px-4 text-sm font-semibold text-[#6B4EFF] hover:text-[#5A3EEE] transition-colors whitespace-nowrap cursor-pointer"
            >
              For Advertisers
            </Link>
          ) : (
            <Link
              to="/"
              className="flex items-center justify-center h-11 px-4 text-sm font-semibold text-[#6B4EFF] hover:text-[#5A3EEE] transition-colors whitespace-nowrap cursor-pointer"
            >
              For Facilities
            </Link>
          )}
        </div>
      </nav>

      {/* ========== DESKTOP NAVIGATION (unchanged) ========== */}
      <nav
        className={`hidden md:block fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center cursor-pointer">
              <Logo isScrolled={isScrolled} />
            </Link>

            <div className="flex items-center space-x-8">
              {/* ------- Navigation links ------- */}
              {!isAdvertisersPage ? (
                <>
                  <button
                    onClick={() => scrollToSection('why-partner')}
                    className={`text-sm font-medium transition-colors hover:text-[#6B4EFF] whitespace-nowrap cursor-pointer ${
                      isScrolled ? 'text-gray-700' : 'text-white'
                    }`}
                  >
                    Why partner with us
                  </button>

                  <button
                    onClick={() => scrollToSection('how-its-funded')}
                    className={`text-sm font-medium transition-colors hover:text-[#6B4EFF] whitespace-nowrap cursor-pointer ${
                      isScrolled ? 'text-gray-700' : 'text-white'
                    }`}
                  >
                    How it&apos;s funded
                  </button>

                  <Link
                    to="/advertisers"
                    className={`text-sm font-medium transition-colors hover:text-[#6B4EFF] whitespace-nowrap cursor-pointer ${
                      isScrolled ? 'text-gray-700' : 'text-white'
                    }`}
                  >
                    For Advertisers
                  </Link>
                </>
              ) : (
                <Link
                  to="/"
                  className={`text-sm font-medium transition-colors hover:text-[#6B4EFF] whitespace-nowrap cursor-pointer ${
                    isScrolled ? 'text-gray-700' : 'text-white'
                  }`}
                >
                  For Facilities
                </Link>
              )}

              {/* ------- Call‑to‑action button ------- */}
              {!isAdvertisersPage ? (
                <button
                  onClick={() => scrollToSection('request-sample')}
                  className="bg-gradient-to-r from-[#6B4EFF] to-[#FF4D7D] text-white px-6 py-2.5 rounded-lg font-medium hover:from-[#5A3EEE] hover:to-[#EE3C6C] transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Request Sample
                </button>
              ) : (
                <button
                  onClick={() => scrollToSection('advertiser-form')}
                  className="bg-gradient-to-r from-[#6B4EFF] to-[#FF4D7D] text-white px-6 py-2.5 rounded-lg font-medium hover:from-[#5A3EEE] hover:to-[#EE3C6C] transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Get Started
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
